import { useState, useEffect, useCallback } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { RoleProvider, useRole } from './context/RoleContext';
import { ThemeProvider } from './context/ThemeContext';
import { AccentProvider } from './context/AccentContext';
import { LoginPage } from './pages/LoginPage';
import { PortfolioPage } from './pages/PortfolioPage';
import { ParticipantDrilldown } from './pages/ParticipantDrilldown';
import { AdminPage } from './pages/AdminPage';
import { ProfilePage } from './pages/ProfilePage';
import { EquipePage } from './pages/EquipePage';
import { ConseillersPage } from './pages/ConseillersPage';
import { CommunicationsPage } from './pages/CommunicationsPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { AppShell } from './components/layout/AppShell';
import { I18nProvider, useT, type Lang } from './i18n';
import { api } from './api/client';
import { parseWorkspace } from './workspace';
import type { PortfolioFilters } from './pages/PortfolioPage';

export type Page = 'portfolio' | 'participant' | 'admin' | 'profile' | 'equipe' | 'conseillers' | 'notifications' | 'communications';

/** One open participant drilldown tab, nested under Portfolio in the sidebar. */
export interface ParticipantTab {
  id: number;
  measure?: number;
  /** Family name — known instantly when opened from Portfolio, otherwise
   * filled from the drilldown's own person fetch (onNameLoaded). */
  name?: string;
}

const ADMIN_ONLY_PAGES: Page[] = ['admin'];
const RESPONSABLE_PAGES: Page[] = ['equipe', 'conseillers'];

/** Participant id (+ optional measure) encoded in '#participant?id=..&measure=..'. */
function parseParticipantHash(): { id: number; measure?: number } | null {
  const raw = window.location.hash.slice(1);
  const [page, qs] = raw.split('?');
  if (page !== 'participant') return null;
  const params = new URLSearchParams(qs || '');
  const id = Number(params.get('id'));
  if (!id || Number.isNaN(id)) return null;
  return { id, measure: Number(params.get('measure')) || undefined };
}

function participantHash(id: number, measure?: number): string {
  return `participant?id=${id}${measure != null ? `&measure=${measure}` : ''}`;
}

function AppContent() {
  const { user, loading } = useAuth();
  const { isAdmin, isResponsable, loading: roleLoading } = useRole();
  const { setLang } = useT();
  const [currentPage, setCurrentPage] = useState<Page>('portfolio');
  const [participantTabs, setParticipantTabs] = useState<ParticipantTab[]>([]);
  const [activeParticipantId, setActiveParticipantId] = useState<number | null>(null);

  // ── Workspace persistence (app_user_pref.workspace) ──────────────────────
  // After login, fetch the stored snapshot and restore it: open tabs, the
  // portfolio's filters/sort, and the page the session left off on. Content
  // stays gated behind wsReady so the portfolio mounts WITH its filters
  // instead of flashing defaults first.
  const [wsReady, setWsReady] = useState(false);
  const [portfolioFilters, setPortfolioFilters] = useState<PortfolioFilters | undefined>(undefined);

  // Apply the user's server-stored language preference (canonical, wins over
  // localStorage) and restore the workspace snapshot. Users who never saved a
  // workspace keep the defaults; a failed fetch degrades to no restore.
  useEffect(() => {
    if (!user) { setWsReady(false); return; }
    let cancelled = false;
    setWsReady(false);
    api.getProfile()
      .then((p) => {
        if (cancelled) return;
        if (p.lang === 'fr' || p.lang === 'en') setLang(p.lang as Lang);
        const ws = parseWorkspace(p.workspace);
        if (ws) {
          setParticipantTabs(ws.tabs);
          setPortfolioFilters(ws.portfolio);
          // An explicit deep link in the URL wins over the saved page.
          const hashPage = window.location.hash.slice(1).split('?')[0];
          if (!hashPage || hashPage === 'portfolio') {
            if (ws.page === 'participant' && ws.activeParticipantId != null) {
              const tab = ws.tabs.find(t => t.id === ws.activeParticipantId);
              window.location.hash = participantHash(ws.activeParticipantId, tab?.measure);
            } else if (ws.page !== 'portfolio') {
              window.location.hash = ws.page;
            }
          }
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setWsReady(true); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Save the workspace (debounced) whenever something in it changes. Tab
  // names are stripped — they re-resolve from V2.1 on restore.
  useEffect(() => {
    if (!user || !wsReady || !portfolioFilters) return;
    const snapshot = {
      tabs: participantTabs.map(({ id, measure }) => ({ id, ...(measure != null ? { measure } : {}) })),
      portfolio: portfolioFilters,
      page: currentPage,
      activeParticipantId,
    };
    const timer = setTimeout(() => { api.setWorkspace(snapshot).catch(() => {}); }, 800);
    return () => clearTimeout(timer);
  }, [user?.id, wsReady, portfolioFilters, participantTabs, currentPage, activeParticipantId]);

  useEffect(() => {
    const handleHashChange = () => {
      const raw = window.location.hash.slice(1) || 'portfolio';
      const page = raw.split('?')[0] as Page;

      // Admin-only pages
      if (!isAdmin && ADMIN_ONLY_PAGES.includes(page)) {
        window.location.hash = 'portfolio';
        return;
      }

      // Responsable-only pages
      if (!isResponsable && RESPONSABLE_PAGES.includes(page)) {
        window.location.hash = 'portfolio';
        return;
      }

      // Landing on '#participant?id=..' opens (or activates) that
      // participant's tab; every other page deactivates the tabs.
      const p = parseParticipantHash();
      if (p) {
        setParticipantTabs(tabs =>
          tabs.some(tab => tab.id === p.id) ? tabs : [...tabs, { id: p.id, measure: p.measure }]
        );
        setActiveParticipantId(p.id);
      } else {
        setActiveParticipantId(null);
      }

      setCurrentPage(page);
    };

    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [isAdmin, isResponsable]);

  const navigate = (page: Page) => {
    window.location.hash = page;
  };

  // Open (or re-focus) a participant tab from the portfolio — the family name
  // comes from the clicked row, so the tab is labeled instantly.
  const openParticipant = useCallback((id: number, measure: number | undefined, name?: string) => {
    const m = measure ?? undefined; // rows can carry null for measure_id
    setParticipantTabs(tabs => {
      if (!tabs.some(tab => tab.id === id)) return [...tabs, { id, measure: m, name }];
      // Same participant, different measure row → keep the tab's measure in
      // sync with what was clicked.
      if (m != null && !tabs.some(tab => tab.id === id && tab.measure === m)) {
        return tabs.map(tab => (tab.id === id ? { ...tab, measure: m } : tab));
      }
      return tabs;
    });
    window.location.hash = participantHash(id, m);
  }, []);

  const activateParticipantTab = useCallback((tab: ParticipantTab) => {
    window.location.hash = participantHash(tab.id, tab.measure);
  }, []);

  // Close a tab; if it was the visible one, fall through to the most recently
  // opened remaining tab, or back to the portfolio.
  const closeParticipantTab = useCallback((id: number) => {
    setParticipantTabs(tabs => tabs.filter(tab => tab.id !== id));
    if (currentPage === 'participant' && activeParticipantId === id) {
      const rest = participantTabs.filter(tab => tab.id !== id);
      const next = rest[rest.length - 1];
      window.location.hash = next ? participantHash(next.id, next.measure) : 'portfolio';
    }
  }, [currentPage, activeParticipantId, participantTabs]);

  // The drilldown already fetches the person on mount; reporting the family
  // name here labels tabs opened without one (deep links, equipe, alerts).
  const setParticipantName = useCallback((id: number, name: string) => {
    setParticipantTabs(tabs =>
      tabs.some(tab => tab.id === id && tab.name === name)
        ? tabs
        : tabs.map(tab => (tab.id === id ? { ...tab, name } : tab))
    );
  }, []);

  if (loading || roleLoading) {
    return (
      <div className="login-page">
        <div className="spinner" />
      </div>
    );
  }

  if (!user) {
    return <LoginPage />;
  }

  // One quick profile fetch gates the shell so the workspace (filters, tabs,
  // last page) is in hand before the first page renders.
  if (!wsReady) {
    return (
      <div className="login-page">
        <div className="spinner" />
      </div>
    );
  }

  const renderPage = () => {
    switch (currentPage) {
      case 'admin':
        return <AdminPage />;
      case 'profile':
        return <ProfilePage />;
      case 'equipe':
        return <EquipePage />;
      case 'conseillers':
        return <ConseillersPage />;
      case 'notifications':
        return <NotificationsPage />;
      case 'communications':
        return <CommunicationsPage />;
      default:
        return null;
    }
  };

  // Portfolio stays mounted for the whole session (hidden while another page or
  // participant tab is on top), so its filters/sort survive any round-trip —
  // including the render between "last tab closed" and the resulting hashchange.
  // Open drilldowns stay mounted the same way. `active` re-fetches the
  // portfolio each time it becomes the visible page again.
  const showPortfolio = currentPage === 'portfolio';
  const onParticipantPage = currentPage === 'participant';

  return (
    <ThemeProvider>
      <AccentProvider>
        <AppShell
          currentPage={currentPage}
          onNavigate={navigate}
          participantTabs={participantTabs}
          activeParticipantId={activeParticipantId}
          onSelectParticipantTab={activateParticipantTab}
          onCloseParticipantTab={closeParticipantTab}
        >
          <div style={{ display: showPortfolio ? undefined : 'none' }}>
            <PortfolioPage
              active={showPortfolio}
              onOpenParticipant={openParticipant}
              initialFilters={portfolioFilters}
              onFiltersChange={setPortfolioFilters}
            />
          </div>
          {participantTabs.map(tab => (
            <div
              key={tab.id}
              style={{ display: onParticipantPage && activeParticipantId === tab.id ? undefined : 'none' }}
            >
              <ParticipantDrilldown
                participantId={tab.id}
                measureId={tab.measure}
                onNameLoaded={setParticipantName}
              />
            </div>
          ))}
          {!showPortfolio && !onParticipantPage && renderPage()}
        </AppShell>
      </AccentProvider>
    </ThemeProvider>
  );
}

export function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <RoleProvider>
          <AppContent />
        </RoleProvider>
      </AuthProvider>
    </I18nProvider>
  );
}
